import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileUp, Loader2, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface InsuranceSectionProps {
  insuranceProvider: string;
  setInsuranceProvider: (value: string) => void;
  policyNumber: string;
  setPolicyNumber: (value: string) => void;
  insuranceProofFile: File | null;
  setInsuranceProofFile: (file: File | null) => void;
  insuranceProofPreview: string | null;
  setInsuranceProofPreview: (preview: string | null) => void;
  onSubmit: () => void;
  isLoading?: boolean;
}

const InsuranceSection: React.FC<InsuranceSectionProps> = ({
  insuranceProvider,
  setInsuranceProvider,
  policyNumber,
  setPolicyNumber,
  insuranceProofFile,
  setInsuranceProofFile,
  insuranceProofPreview,
  setInsuranceProofPreview,
  onSubmit,
  isLoading = false,
}) => {
  const { toast } = useToast();

  const handleInsuranceProofChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setInsuranceProofFile(file);
      const fileUrl = URL.createObjectURL(file);
      setInsuranceProofPreview(fileUrl);
    }
  };

  const triggerFileInput = (inputId: string) => {
    const input = document.getElementById(inputId) as HTMLInputElement;
    if (input) {
      input.click();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!insuranceProvider || !policyNumber || !insuranceProofFile) {
      toast({
        title: "Missing Information",
        description:
          "Please provide your insurance provider, policy number, and upload proof of insurance",
        variant: "destructive",
      });
      return;
    }

    onSubmit();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Insurance Check</CardTitle>
        <CardDescription>
          Please provide your insurance details and upload proof of insurance
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="insuranceProvider">Insurance Provider</Label>
            <Input
              id="insuranceProvider"
              value={insuranceProvider}
              onChange={(e) => setInsuranceProvider(e.target.value)}
              placeholder="Enter your insurance provider name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="policyNumber">Policy Number</Label>
            <Input
              id="policyNumber"
              value={policyNumber}
              onChange={(e) => setPolicyNumber(e.target.value)}
              placeholder="Enter your policy number"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="insuranceProof">Proof of Insurance</Label>
            <div
              className="border-2 border-dashed border-gray-300 rounded-md p-6 flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 transition-colors"
              onClick={() => triggerFileInput("insuranceProof")}
            >
              {insuranceProofPreview ? (
                <div className="flex flex-col items-center space-y-4">
                  <img
                    src={insuranceProofPreview}
                    alt="Insurance proof preview"
                    className="max-h-48 object-contain"
                  />
                  <div className="flex space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setInsuranceProofFile(null);
                        setInsuranceProofPreview(null);
                      }}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <FileUp className="h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-sm text-gray-500 mb-2">
                    Click to upload insurance certificate
                  </p>
                  <p className="text-xs text-gray-400">
                    PNG, JPG or PDF (max. 10MB)
                  </p>
                </>
              )}
            </div>
            <Input
              id="insuranceProof"
              type="file"
              className="hidden"
              onChange={handleInsuranceProofChange}
              accept="image/png,image/jpeg,application/pdf"
              required
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-honeycomb hover:bg-amber-500 text-white"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit Insurance Information"
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default InsuranceSection;
